import { DeepPartial } from '@givebacks/utils/types';
import { UseMutationOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { useStoreApi } from '../api';
import { storeKeys } from '../query-key-factory';
import { Cart, CartItem } from './cart.types';

interface UpdateCart extends DeepPartial<Omit<Cart, 'price'>> {
  credit_to?: CartItem['credit_to'];
  credit_write_in?: CartItem['credit_write_in'];
  store_item_id?: string;
}
type UpdateCartRequest = {
  cart?: UpdateCart;
  uuid: string;
  storeItemId?: string;
  causeId?: string;
  credit_to?: CartItem['credit_to'];
  credit_write_in?: CartItem['credit_write_in'];
  /**
   * The Id you want to invalidate
   * When using on management side, this is required
   */
  scopeId?: string;
};
type UpdateCartResponse = {
  cart: Cart;
};

const useCartUpdate = () => {
  const StoreApi = useStoreApi();
  return async (updateRequest: UpdateCartRequest) => {
    const { causeId, cart } = updateRequest;
    const { data } = await StoreApi.put<UpdateCartResponse>(`/carts/${updateRequest.uuid}`, {
      cart,
      cause_id: causeId,
    });

    return data.cart;
  };
};

type UseUpdateCart = {
  mutationKey?: UseMutationOptions['mutationKey'];
};

/** Updates a given cart.Allows deeply nested updating */
const useUpdateCart = ({ mutationKey }: UseUpdateCart = {}) => {
  const queryClient = useQueryClient();
  const updateCart = useCartUpdate();

  return useMutation({
    mutationKey: mutationKey,
    mutationFn: updateCart,
    onSuccess: (data, variables) => {
      const { scopeId = 'user' } = variables;
      // We can tie an update to either the causeId, or 'user' if we don't have a causeId
      const invalidationKey = storeKeys.id(scopeId).carts.id(data.uuid).detail;
      return queryClient.invalidateQueries({ queryKey: invalidationKey });
    },
  });
};

/**
 *
 * Used to update a nested cart Item
 */
const useUpdateCartsCartItem = () => {
  const queryClient = useQueryClient();
  const updateCart = useCartUpdate();

  return useMutation({
    mutationFn: updateCart,
    onSuccess: (data, variables) => {
      const { scopeId = 'user' } = variables;
      // We can tie an update to either the causeId, or 'user' if we don't have a causeId
      const invalidationKey = storeKeys.id(scopeId).carts.id(data.uuid).detail;
      return queryClient.invalidateQueries({ queryKey: invalidationKey });
    },
  });
};

const useUpdateCartPledgeStatus = () => {
  const queryClient = useQueryClient();
  const updateCart = useCartUpdate();

  return useMutation({
    mutationFn: updateCart,
    onMutate: async (request) => {
      const { scopeId = 'user' } = request;

      const invalidationKey = storeKeys.id(scopeId).carts.id(request.uuid).detail;
      await queryClient.cancelQueries({ queryKey: invalidationKey });

      const previousCart = queryClient.getQueryData(invalidationKey);
      const newCart = request.cart;

      // Optimistically update to the new value
      queryClient.setQueryData(invalidationKey, (prev?: DeepPartial<Cart>) =>
        prev ? { ...prev, status: newCart?.status } : prev
      );

      return { previousCart, newCart };
    },
    onError(_error, _variables, context) {
      if (!context) return;
      const { scopeId = 'user', uuid } = _variables;

      const invalidationKey = storeKeys.id(scopeId).carts.id(uuid).detail;

      // Optimistically update to the new value
      queryClient.setQueryData(invalidationKey, context.previousCart);
    },
    onSettled: (data, _error, variables) => {
      const { scopeId = 'user' } = variables;
      if (!data) return;
      // We can tie an update to either the causeId, or 'user' if we don't have a causeId
      const invalidationKey = storeKeys.id(scopeId).carts.id(data.uuid).detail;
      return queryClient.invalidateQueries({ queryKey: invalidationKey });
    },
  });
};

const useResendCartReceipt = () => {
  const StoreApi = useStoreApi();

  const sendCartReceipt = async ({ cartId, causeId }: { cartId?: string; causeId?: string }) => {
    const { data } = await StoreApi.post(`/carts/${cartId}/receipt`, {
      cause_id: causeId,
      uuid: cartId,
    });

    return data;
  };

  return useMutation({
    mutationFn: sendCartReceipt
  });
};

export {
  useCartUpdate,
  useResendCartReceipt,
  useUpdateCart,
  useUpdateCartPledgeStatus,
  useUpdateCartsCartItem
};

